import { Input } from 'antd';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SEARCH_INPUT_PROPS } from '../policies/search-input-props';

const SEARCH_DEBOUNCE_MS = 300;

interface ListSearchProps {
  /** The committed search from the list query (the URL), not the keystroke-by-keystroke text. */
  value: string | undefined;
  placeholder: string;
  onSearch: (q: string | undefined) => void;
}

/** Search box for a list screen: commits after a pause in typing, or at once on Enter or clear. */
export function ListSearch({ value, placeholder, onSearch }: ListSearchProps) {
  const [text, setText] = useState(value ?? '');
  const [committed, setCommitted] = useState(value);

  // Follow the URL when it changes underneath us (Back button, a shared link) — adjusting state during render, not in an effect.
  if (value !== committed) {
    setCommitted(value);
    setText(value ?? '');
  }

  useEffect(() => {
    const term = text.trim() || undefined;
    if (term === committed) return;
    const timer = setTimeout(() => onSearch(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text, committed, onSearch]);

  return (
    <Input
      {...SEARCH_INPUT_PROPS}
      allowClear
      aria-label={placeholder}
      className="w-full md:max-w-sm"
      placeholder={placeholder}
      prefix={<Search className="size-4 text-muted" />}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        if (event.target.value === '') onSearch(undefined);
      }}
      onPressEnter={() => onSearch(text.trim() || undefined)}
    />
  );
}
